import React, { Suspense } from "react";

import { useTranslation } from "react-i18next";
import { Routes, Route } from "react-router-dom-v5-compat";

import { NavBarProvider } from "./Atoms/Navbar";
import { brandForContext, BrandContext } from "./BrandContext";
import { BustleLayout } from "./BustleLayout";
import Loading from "./Loading";
import MainPage from "./MainPage";
import BillSplitLayout from "./Pages/BillSplit/BillSplitLayout";
import { JoinBillSplitSession } from "./Pages/BillSplit/JoinBillSplitSession";
import OwnerReview from "./Pages/BillSplit/OwnerReview";
import ReviewBillSplitSesson from "./Pages/BillSplit/ReviewBillSplitSession";
import { BillSplitSelectOrderItems } from "./Pages/BillSplit/SelectOrderItems";
import { Share } from "./Pages/BillSplit/Share";
import { StartBillSplitSession } from "./Pages/BillSplit/StartBillSplitSession";
import { EditCartItem, NewCartItem } from "./Pages/CartItem/CartItem";
import Cart from "./Pages/Checkout/Checkout";
import CheckoutSuccess, {
  TabCheckoutSuccess,
} from "./Pages/Checkout/CheckoutSuccess";
import ClearCart from "./Pages/Checkout/ClearCart";
import CheckoutLayout from "./Pages/Checkout/Layout";
import OrderPlaced from "./Pages/Checkout/OrderPlaced";
import LookupVoucherCode from "./Pages/Checkout/VoucherAssignments/LookupVoucherCode";
import { CustomerForm } from "./Pages/Customer/CustomerForm";
import { BillReview } from "./Pages/CustomerTab/BillReview";
import Section from "./Pages/Sections/Section";
import { SectionLayout } from "./Pages/Sections/SectionLayout";
import { useSession } from "./Pages/Session/SessionContext";
import { getIsWishlist } from "./Pages/Session/sessionSelectors";
import LandingPage from "./Pages/VoucherCart/LandingPage";
import VoucherCartItemForm from "./Pages/VoucherCart/VoucherCartItemForm";
import VoucherCheckout from "./Pages/VoucherCart/VoucherCheckout";
import VoucherCheckoutSuccess from "./Pages/VoucherCart/VoucherCheckoutSuccess";
import VouchersLayout from "./Pages/VoucherCart/VouchersLayout";
import Promotions, { Promotion } from "./Promotions";
import ProtectedMenuRoute from "./ProtectedMenuRoute";
import { ThankYou } from "./ThankYou";
import { VenueContext, useVenue } from "./VenueContext";
import { VenueLayout } from "./VenueLayout";
import type { Venue } from "./schema";

export const MobileMenuProviders = ({
  venue,
  children,
}: {
  venue: Venue;
  children: React.ReactNode;
}) => {
  const brand = brandForContext(venue.venueBrand);
  return (
    <VenueContext.Provider value={venue}>
      <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>
    </VenueContext.Provider>
  );
};

export const MobileOrderingMenu = () => {
  const isWishlist = getIsWishlist(useSession(), useVenue());
  const { t } = useTranslation();
  return (
    <Routes>
      <Route element={<BustleLayout />}>
        <Route path="thank-you" element={<ThankYou />} />
      </Route>
      <Route element={<VenueLayout />}>
        <Route element={<ProtectedMenuRoute />}>
          {/*
          Because the root page is subtley different in how
          it handle responsiveness ( It doesn't hide some content )
          it needs to be rendered separately
          */}
          <Route path="/" element={<MainPage isRootPage />}>
            <Route index element={<Promotions />} />
          </Route>

          {/* Menu Content Routes */}
          <Route element={<MainPage />}>
            <Route
              path="order-items/:itemUuid/:cartItemId"
              element={<EditCartItem />}
            />
            <Route element={<Promotion />}>
              <Route path="promotions/:itemUuid" element={<NewCartItem />} />
            </Route>
            <Route path=":sectionUuid" element={<SectionLayout />}>
              <Route index element={<Section />} />
              <Route path=":itemUuid/new" element={<NewCartItem />} />
            </Route>
          </Route>

          {/* Order Routes */}
          <Route
            path="order"
            element={<CheckoutLayout title={t("orderConfirmed.title")} />}
          >
            <Route path=":orderId/ordered" element={<OrderPlaced />} />
          </Route>

          <Route path="clear" element={<ClearCart />} />
          <Route path="thank-you" element={<ThankYou />} />
          <Route
            path="your-order"
            element={
              <CheckoutLayout
                title={isWishlist ? t("cart.wishlistTitle") : t("cart.title")}
              />
            }
          >
            <Route path="pending">
              <Route index element={<Cart />} />
              <Route path="lookup-voucher" element={<LookupVoucherCode />} />
            </Route>

            <Route path="*" element={<div>Your order path not found</div>} />
          </Route>

          {/* Checkout Routes */}
          <Route
            path="cart/checkout/:checkoutId"
            element={<CheckoutLayout title={t("orderConfirmed.title")} />}
          >
            <Route path="success" element={<CheckoutSuccess />} />
          </Route>
          <Route
            path="tab/checkout/:checkoutId"
            element={<CheckoutLayout title={t("paymentConfirmed.title")} />}
          >
            <Route path="success" element={<TabCheckoutSuccess />} />
          </Route>

          {/* Bill Split Routes */}
          <Route path="billSplit" element={<BillSplitLayout />}>
            <Route path="customer" element={<CustomerForm />} />
            <Route path="join" element={<JoinBillSplitSession />} />
            <Route path="start" element={<StartBillSplitSession />} />
            <Route path="review" element={<ReviewBillSplitSesson />} />
            <Route path="ownerReview" element={<OwnerReview />} />
            <Route path="bill" element={<BillReview />} />
            <Route path="share" element={<Share />} />
            <Route path="lookup-voucher" element={<LookupVoucherCode />} />
            <Route index element={<BillSplitSelectOrderItems />} />
          </Route>
        </Route>

        {/* Voucher Routes */}
        <Route path="vouchers">
          <Route index element={<LandingPage />} />
          <Route element={<VouchersLayout />}>
            <Route path="items/new" element={<VoucherCartItemForm />} />
            <Route
              path="items/:voucherCartItemUuid"
              element={<VoucherCartItemForm />}
            />
            <Route
              path="checkout/:voucherCheckoutUuid/success"
              element={<VoucherCheckoutSuccess />}
            />
            <Route path="checkout" element={<VoucherCheckout />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
};

const MobileOrderingMenuContainer = ({ venue }: { venue: Venue }) => {
  return (
    <NavBarProvider>
      <Suspense fallback={<Loading loadingState="LOADING" />}>
        <MobileMenuProviders venue={venue}>
          <MobileOrderingMenu />
        </MobileMenuProviders>
      </Suspense>
    </NavBarProvider>
  );
};
export default MobileOrderingMenuContainer;
